import React from "react";
import { Card, CardBody, Nav, Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnapshot } from "valtio";

import store, {
    setAllCategories,
    setAllHistogram,
    setTop10Categories,
    setTop10Histogram,
} from "@pages/Statistics/store";
import {
    setAdvancedDate,
    setAdvancedLegend,
    setAdvancedTypeParam,
    setSegmentQueryString,
} from "@pages/Statistics/Transactions/store";

import { useResource } from "@hooks/useResource";

import {
    getAdvancedPeriodStringParam,
    getAdvancedSeeMorePeriod,
    getFormattedCategoriesByPeriod,
    getStatisticsData,
    getTypeParam,
} from "@helpers/general";

import ChartWrapper from "@pages/Statistics/CommonCharts/ChartWrapper";
import ErrorPage from "@components/Common/ErrorPage";
import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";

import "@assets/apex-chart.css";

export default function RecapCol() {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const { period, type, advancedDate, selections } = useSelector(
        (state: any) => state
    );

    const {
        products: {
            top10: { categories },
        },
    } = useSnapshot(store);

    const [top10ArticlesData, setTop10ArticlesData] = React.useState<any[]>([]);

    const {
        resourceData: { data: statisticsArticlesData },
        error: statisticsArticlesError,
    } = useResource(
        `${
            process.env.REACT_APP_API_V1_URL
        }/statistics/products/recap?type=${getTypeParam(
            type
        )}&${getAdvancedPeriodStringParam(period, advancedDate)}${
            selections.designation && selections.designation !== "my_favorites"
                ? `&selected=${selections.designation}`
                : ""
        }`
    );

    const {
        resourceData: { data },
    } = useResource(
        `${
            process.env.REACT_APP_API_V1_URL
        }/statistics/products/all/recap?type=${getTypeParam(
            type
        )}&${getAdvancedPeriodStringParam(period, advancedDate)}${
            selections.designation && selections.designation !== "my_favorites"
                ? `&selected=${selections.designation}`
                : ""
        }`
    );

    React.useEffect(() => {
        setTop10Categories(
            getFormattedCategoriesByPeriod(period, advancedDate),
            "products"
        );
    }, [period, advancedDate]);

    React.useEffect(() => {
        setTop10Histogram(statisticsArticlesData, "products");
        setTop10ArticlesData(
            getStatisticsData(
                statisticsArticlesData,
                categories,
                period,
                advancedDate
            )
        );
    }, [statisticsArticlesData, categories, period, advancedDate, t]);

    React.useEffect(() => {
        setAllCategories(
            getFormattedCategoriesByPeriod(period, advancedDate),
            "products"
        );
    }, [period, advancedDate]);

    React.useEffect(() => {
        setAllHistogram(data, "products");
    }, [data]);

    function handleSeeMoreButtonOnClickEvent() {
        let segmentObject = {
            designation: "temp",
            value: [
                {
                    designation: "articles",
                    value: top10ArticlesData.map((product) => product.name),
                },
            ],
        };

        setAdvancedLegend(t("Products"));
        setAdvancedTypeParam(type);
        setSegmentQueryString(encodeURI(JSON.stringify(segmentObject)));

        setAdvancedDate(getAdvancedSeeMorePeriod(period, advancedDate));

        history.push("/statistics-advanced", { prevUrl: location.pathname });
    }

    return (
        <Card>
            <CardBody className="card-body__clz">
                <Nav pills className="navtab-bg float-right d-none">
                    <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={handleSeeMoreButtonOnClickEvent}
                    >
                        {t("See more")}{" "}
                        <i className="mdi mdi-arrow-right ml-1"></i>
                    </Button>
                </Nav>
                <div className="apex-charts apex-chart__clz">
                    {statisticsArticlesError ? (
                        <ErrorPage />
                    ) : (
                        <CustomErrorBoundary>
                            <ChartWrapper
                                series={top10ArticlesData}
                                categories={categories}
                            />
                        </CustomErrorBoundary>
                    )}
                </div>
            </CardBody>
        </Card>
    );
}
